import axios from "@/plugins/axios.js";
import api from "@/api.js";

export const ACCESS_CONTROL_BASE_PATH = "/api/v1/access-control/devices";
export const ACCESS_CONTROL_VENDORS_PATH = "/api/v1/access-control/vendors";
export const ACCESS_CONTROL_SERVER_LOCATION_PATH = "/api/v1/system/location";
export const ACCESS_CONTROL_EVENTS_FIND_PATH =
	"/api/v1/access-control/events/find/";

const apiUrl = () => (api && api.url ? api.url : "");

export const redactRtspCredentials = (value) => {
	if (!value || typeof value !== "string") return value;
	try {
		const url = new URL(value);
		if (url.protocol !== "rtsp:") return value;
		if (!url.username && !url.password) return value;
		url.username = "***";
		url.password = url.password ? "***" : "";
		return url.toString();
	} catch (e) {
		return value.replace(/^(rtsp:\/\/)([^/@]+)@/i, "$1***:***@");
	}
};

export const sanitizeAccessControlDevice = (device) => {
	if (!device || typeof device !== "object") return device;

	if (Array.isArray(device)) {
		return device.map((item) => sanitizeAccessControlDevice(item));
	}

	const sanitized = { ...device };
	[
		"password",
		"modelData",
		"faceData",
		"FaceData",
		"photoData",
		"PhotoData",
		"photoBase64",
		"imgBase64",
		"template",
		"faceURL",
		"FaceURL",
		"faceUrl",
		"pictureURL",
		"photoURL",
		"imageURL",
		"imgUrl",
	].forEach((field) => {
		delete sanitized[field];
	});

	["rtsp", "rtspUrl", "streamUrl"].forEach((field) => {
		if (sanitized[field])
			sanitized[field] = redactRtspCredentials(sanitized[field]);
	});

	Object.keys(sanitized).forEach((key) => {
		if (sanitized[key] && typeof sanitized[key] === "object") {
			sanitized[key] = sanitizeAccessControlDevice(sanitized[key]);
		}
	});

	return sanitized;
};

export const sanitizeAccessControlResponse = (response) => {
	if (!response || typeof response !== "object") return response;

	return {
		...response,
		data: sanitizeAccessControlDevice(response.data),
	};
};

const pickDefined = (payload = {}, fields = []) => {
	return fields.reduce((body, field) => {
		if (
			payload[field] !== undefined &&
			payload[field] !== null &&
			payload[field] !== ""
		) {
			body[field] = payload[field];
		}
		return body;
	}, {});
};

export const normalizeAccessControlVendor = (vendor) => {
	if (vendor === undefined || vendor === null || String(vendor).trim() === "")
		return "";
	const normalized = String(vendor).trim().toLowerCase();
	return ["dahua", "hikvision"].includes(normalized) ? normalized : "";
};

const buildCreatePayload = (payload = {}) => {
	const body = pickDefined(payload, [
		"name",
		"host",
		"ip",
		"username",
		"password",
		"longitude",
		"latitude",
		"rtsp",
		"vendor",
		"model",
		"serial",
	]);
	const vendor = normalizeAccessControlVendor(body.vendor);

	if (!body.host && body.ip) {
		body.host = body.ip;
		delete body.ip;
	}
	if (vendor) {
		body.vendor = vendor;
	} else {
		delete body.vendor;
	}

	return body;
};

const buildUpdatePayload = (payload = {}) => {
	const body = buildCreatePayload(payload);
	delete body.deviceId;
	if (
		Object.hasOwn(payload, "rtsp") &&
		(payload.rtsp === "" || payload.rtsp === null)
	) {
		body.rtsp = "";
	}
	return body;
};

const buildProbePayload = (payload = {}) => {
	const body = pickDefined(payload, [
		"vendor",
		"model",
		"host",
		"ip",
		"username",
		"password",
	]);
	const vendor = normalizeAccessControlVendor(body.vendor);
	if (!body.host && body.ip) {
		body.host = body.ip;
		delete body.ip;
	}
	if (vendor) {
		body.vendor = vendor;
	} else {
		delete body.vendor;
	}
	return body;
};

class AccessControlService {
	actionPath(deviceId, path = "") {
		return `${apiUrl()}${ACCESS_CONTROL_BASE_PATH}/${encodeURIComponent(deviceId)}${path}`;
	}

	listDevices(params) {
		return axios
			.get(`${apiUrl()}${ACCESS_CONTROL_BASE_PATH}`, { params })
			.then(sanitizeAccessControlResponse);
	}

	listVendors() {
		return axios
			.get(`${apiUrl()}${ACCESS_CONTROL_VENDORS_PATH}`)
			.then(sanitizeAccessControlResponse);
	}

	listVendorModels(vendor) {
		return axios
			.get(
				`${apiUrl()}${ACCESS_CONTROL_VENDORS_PATH}/${encodeURIComponent(vendor)}/models`,
			)
			.then(sanitizeAccessControlResponse);
	}

	probeDevice(payload) {
		return axios
			.post(
				`${apiUrl()}${ACCESS_CONTROL_BASE_PATH}/probe`,
				buildProbePayload(payload),
			)
			.then(sanitizeAccessControlResponse);
	}

	getServerLocation() {
		return axios
			.get(`${apiUrl()}${ACCESS_CONTROL_SERVER_LOCATION_PATH}`)
			.then(sanitizeAccessControlResponse);
	}

	getDevice(deviceId) {
		return axios
			.get(
				`${apiUrl()}${ACCESS_CONTROL_BASE_PATH}/${encodeURIComponent(deviceId)}`,
			)
			.then(sanitizeAccessControlResponse);
	}

	getDevicePassword(deviceId) {
		return axios.get(
			`${apiUrl()}${ACCESS_CONTROL_BASE_PATH}/${encodeURIComponent(deviceId)}/password`,
		);
	}

	createDevice(payload) {
		return axios
			.post(
				`${apiUrl()}${ACCESS_CONTROL_BASE_PATH}`,
				buildCreatePayload(payload),
			)
			.then(sanitizeAccessControlResponse);
	}

	updateDevice(deviceId, payload) {
		return axios
			.put(
				`${apiUrl()}${ACCESS_CONTROL_BASE_PATH}/${encodeURIComponent(deviceId)}`,
				buildUpdatePayload(payload),
			)
			.then(sanitizeAccessControlResponse);
	}

	createUser(deviceId, payload) {
		return axios
			.post(this.actionPath(deviceId, "/users"), payload)
			.then(sanitizeAccessControlResponse);
	}

	updateUser(deviceId, userId, payload) {
		return axios
			.put(
				this.actionPath(deviceId, `/users/${encodeURIComponent(userId)}`),
				payload,
			)
			.then(sanitizeAccessControlResponse);
	}

	createCard(deviceId, payload) {
		return axios
			.post(this.actionPath(deviceId, "/cards"), payload)
			.then(sanitizeAccessControlResponse);
	}

	getCardByUserId(deviceId, userId) {
		return axios
			.get(
				this.actionPath(
					deviceId,
					`/cards/by-user/${encodeURIComponent(userId)}`,
				),
			)
			.then(sanitizeAccessControlResponse);
	}

	updateCard(deviceId, recno, payload) {
		return axios
			.put(
				this.actionPath(deviceId, `/cards/${encodeURIComponent(recno)}`),
				payload,
			)
			.then(sanitizeAccessControlResponse);
	}

	removeCard(deviceId, recno) {
		return axios
			.delete(this.actionPath(deviceId, `/cards/${encodeURIComponent(recno)}`))
			.then(sanitizeAccessControlResponse);
	}

	uploadFace(deviceId, payload) {
		return axios
			.post(this.actionPath(deviceId, "/faces"), payload)
			.then(sanitizeAccessControlResponse);
	}

	listFaces(deviceId, userId) {
		return axios
			.get(this.actionPath(deviceId, `/faces/${encodeURIComponent(userId)}`))
			.then(sanitizeAccessControlResponse);
	}

	getFaceRoster(deviceId, limit = 100) {
		return axios
			.get(this.actionPath(deviceId, "/face-roster"), { params: { limit } })
			.then(sanitizeAccessControlResponse);
	}

	importFaceRoster(deviceId, payload) {
		return axios
			.post(this.actionPath(deviceId, "/face-roster/import"), payload)
			.then(sanitizeAccessControlResponse);
	}

	syncFaceRoster(deviceId) {
		return axios
			.post(this.actionPath(deviceId, "/face-roster/sync-terminal-users"))
			.then(sanitizeAccessControlResponse);
	}

	findEvents(dataWhere = {}, options = {}) {
		const page = options.page || 1;
		const limit = options.limit || 1000;
		return axios.post(
			`${apiUrl()}${ACCESS_CONTROL_EVENTS_FIND_PATH}?page=${page}&limit=${limit}`,
			{ data_where: dataWhere },
		);
	}

	updateFace(deviceId, userId, payload) {
		return axios
			.put(
				this.actionPath(deviceId, `/faces/${encodeURIComponent(userId)}`),
				payload,
			)
			.then(sanitizeAccessControlResponse);
	}

	removeFace(deviceId, userId, faceId) {
		return axios
			.delete(
				this.actionPath(
					deviceId,
					`/faces/${encodeURIComponent(userId)}/${encodeURIComponent(faceId)}`,
				),
			)
			.then(sanitizeAccessControlResponse);
	}

	openDoor(deviceId, payload = {}) {
		return axios
			.post(this.actionPath(deviceId, "/door/open"), payload)
			.then(sanitizeAccessControlResponse);
	}

	controlRelay(deviceId, relayId, payload = {}) {
		return axios
			.post(
				this.actionPath(
					deviceId,
					`/relay/${encodeURIComponent(relayId)}/control`,
				),
				payload,
			)
			.then(sanitizeAccessControlResponse);
	}
}

export default new AccessControlService();
